import { useCallback, useEffect, useState } from "react";
import { NavLink, Navigate, Route, Routes } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { errMsg } from "../ui";
import { UsageOverview } from "./UsageOverview";
import { UsageRunDetail } from "./UsageRunDetail";
import { CallBrowser } from "./CallBrowser";
import { UsageSettings } from "./UsageSettings";

const TABS = [
  { to: "", label: "Runs & cycle", end: true, key: "overview" },
  { to: "calls", label: "Call browser", key: "calls" },
  { to: "settings", label: "Settings", key: "settings", edit: true },
];

/** Billing Sync → Usage Charges: WebCDR collection, cycle usage per domain, Stax Bill usage amounts, call browser. */
export function UsageCharges() {
  const [ov, setOv] = useState(null);
  const load = useCallback(() => api.get("/usage-charges/overview").then(({ data }) => setOv(data))
    .catch((e) => toast.error(errMsg(e, "Could not load Usage Charges"))), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!ov?.running) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [ov?.running, load]);
  if (!ov) return <Loader2 className="mx-auto mt-12 h-6 w-6 animate-spin text-slate-300" />;
  return (
    <div className="space-y-4" data-testid="usage-charges">
      <div>
        <h2 className="font-heading text-lg font-semibold text-slate-900">Usage Charges</h2>
        <p className="text-sm text-slate-500">WebCDR rated calls are saved to the billing database every day, and each domain's long-distance and toll-free usage for the open billing cycle goes on its Stax Bill subscription.</p>
      </div>
      <nav className="flex gap-1 border-b border-slate-200" data-testid="uc-tabs">
        {TABS.filter((t) => !t.edit || ov.can_edit).map((t) => (
          <NavLink key={t.key} to={`/billing-sync/usage-charges${t.to ? `/${t.to}` : ""}`} end={t.end} data-testid={`uc-tab-${t.key}`}
                   className={({ isActive }) => `-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${isActive ? "border-emerald-600 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            {t.label}</NavLink>))}
      </nav>
      <Routes>
        <Route index element={<UsageOverview ov={ov} reload={load} />} />
        <Route path="runs/:rid" element={<UsageRunDetail />} />
        <Route path="calls" element={<CallBrowser ov={ov} />} />
        {ov.can_edit && <Route path="settings" element={<UsageSettings ov={ov} reload={load} />} />}
        <Route path="*" element={<Navigate to="/billing-sync/usage-charges" replace />} />
      </Routes>
    </div>
  );
}
